import React, { useState, useRef, useEffect } from "react";
import { array, string } from "prop-types";
import cx from "classnames";
import throttle from "lodash.throttle";

import Icon from "../Icon/Icon.jsx";
import useClickAway from "../../common/useClickAway.js";
import inPageNavigation from "../../common/inPageNavigation.js";

const propTypes = {
  links: array.isRequired,
  label: string,
};

const { scrollPageTo } = inPageNavigation();

const JumpNavigation = (props) => {
  const { links, label } = props;

  const menuButtonRef = useRef();

  const [focusIndex, setFocusIndex] = useState(0);
  const [activeLinkIndex, setActiveLinkIndex] = useState(() => {
    const hash = window.location.hash;
    if (hash) {
      const link = links.find((link) => {
        return link.href === hash;
      });
      return link ? link.index : -1;
    } else {
      return -1;
    }
  });

  const [showMenu, setShowMenu] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const dropDownRef = useClickAway({
    isOpen: showMenu,
    setOpen: () => {
      setShowMenu(false);
    },
  });

  const closeMenu = () => {
    setShowMenu(false);
    setFocusIndex(0);
    menuButtonRef.current.focus();
  };

  const toggleMenu = (e) => {
    e.preventDefault();
    if (showMenu) {
      closeMenu();
    } else {
      setFocusIndex(0);
      setShowMenu(true);
    }
  };

  const handleLinkClick = (e, index) => {
    e.preventDefault();

    const target = links[index].element;
    const customScrollOffset = target.dataset.customScrollOffset;

    setShowMenu(false);
    setIsScrolling(true);
    setActiveLinkIndex(index);

    if (customScrollOffset) {
      scrollPageTo(target, parseInt(customScrollOffset, 10));
    } else {
      scrollPageTo(target);
    }

    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  const handleMenuButtonKeyboard = (e) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeMenu();
        break;
      case "Enter":
        e.preventDefault();
        toggleMenu(e);
        break;
      default:
    }
  };

  const handleMenuItemKeyboard = (e) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeMenu();
        break;
      case "Tab":
        e.preventDefault();
        if (e.shiftKey) {
          if (focusIndex === 0) {
            closeMenu();
          } else {
            setFocusIndex(focusIndex - 1);
          }
        } else {
          if (focusIndex === links.length - 1) {
            closeMenu();
          } else {
            setFocusIndex(focusIndex + 1);
          }
        }
        break;
      case "Up":
      case "ArrowUp":
        e.preventDefault();
        if (focusIndex === 0) {
          setFocusIndex(links.length - 1);
        } else {
          setFocusIndex(focusIndex - 1);
        }
        break;
      case "Down":
      case "ArrowDown":
        e.preventDefault();
        if (focusIndex === links.length - 1) {
          setFocusIndex(0);
        } else {
          setFocusIndex(focusIndex + 1);
        }
        break;

      default:
    }
  };

  useEffect(() => {
    const throttleHandleScroll = throttle(() => {
      if (isScrolling) {
        return;
      }

      const index = [...links].reduce((index, target) => {
        const { element } = target;
        const rect = element.getBoundingClientRect();
        return rect.top < window.innerHeight / 2 + 100 ? index + 1 : index;
      }, -1);

      if (index !== activeLinkIndex) {
        setActiveLinkIndex(index);
      }
    }, 100);

    window.addEventListener("scroll", throttleHandleScroll);

    return () => {
      window.removeEventListener("scroll", throttleHandleScroll);
    };
  }, [activeLinkIndex, isScrolling, links]);

  const renderLinkItem = (link) => {
    return (
      <li
        key={link.index}
        className={cx("jump-navigation__item", {
          "jump-navigation__item--active": link.index === activeLinkIndex,
        })}
      >
        <a
          aria-current={link.index === activeLinkIndex ? "location" : null}
          className="jump-navigation__link"
          ref={(el) =>
            focusIndex === link.index && showMenu && el && el.focus()
          }
          href={link.href}
          onClick={(e) => handleLinkClick(e, link.index)}
          onKeyDown={(e) => handleMenuItemKeyboard(e, link.index)}
        >
          {link.label}
        </a>
      </li>
    );
  };

  return (
    <nav className="jump-navigation__app" aria-label="In Page Navigation">
      <div className="container">
        <div className="jump-navigation__inner">
          <div className="jump-navigation__drop-down" ref={dropDownRef}>
            <button
              type="button"
              ref={menuButtonRef}
              aria-controls="jumpNavMenu"
              onClick={toggleMenu}
              className="jump-navigation__menu-button"
              aria-expanded={showMenu}
              onKeyDown={(e) => handleMenuButtonKeyboard(e)}
            >
              <span
                aria-hidden="true"
                className="jump-navigation__active-link-label"
              >
                {links[activeLinkIndex === -1 ? 0 : activeLinkIndex].label}
              </span>
              <span className="jump-navigation__menu-label">{label}</span>
              <Icon
                className="jump-navigation__menu-icon"
                iconName="chevron-right"
              />
            </button>
            <div
              className={cx("jump-navigation__menu", {
                "jump-navigation__menu--is-open": showMenu,
              })}
              id="jumpNavMenu"
            >
              <ul className="jump-navigation__items">
                {links.map(renderLinkItem)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
JumpNavigation.propTypes = propTypes;
export default JumpNavigation;
